'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { UpdateGroupParams } from '@/types/group'
import { useGroup, useUpdateGroup } from '@/hooks'
import { FormModeEnum } from '@/enums'
import { GroupForm, type GroupFormRef } from '@/components/partials/groups'
import { ContentError, FormSkeleton } from '@/components/partials'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const groupFormRef = useRef<GroupFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: group, isLoading, error } = useGroup(id)
  const { mutate: updateGroup, isPending } = useUpdateGroup()

  const handleSubmit = (data: UpdateGroupParams) => {
    updateGroup(
      { id, params: data },
      {
        onSuccess: async updatedGroup => {
          setIsRedirecting(true)
          queryClient.setQueryData(['group', id], updatedGroup)
          await queryClient.invalidateQueries({ queryKey: ['groups'] })
          toast.success(t('updated_successfully'))
          router.push(`/users/groups/${id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (groupFormRef.current) {
        groupFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/users/groups/${id}`)
  }

  if (isLoading) {
    return <FormSkeleton fieldRows={2} showTranslations={false} />
  }

  if (error || !group) {
    return <ContentError error={error} listUrl="/users/groups" />
  }

  return (
    <GroupForm
      ref={groupFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={group}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
